import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm, type Supplier } from '../api/types'
import { toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Row, Spinner, TextArea, TextInput,
} from '@shop/ui'

/**
 * المورّدون — قائمةٌ وبطاقةٌ وأرشفة.
 *
 * <b>ولا زرَّ حذف.</b> مورّدٌ حُذف يترك كلَّ أمرِ شراءٍ أشار إليه بلا مرجع،
 * وتقريرَ السنة الماضية بلا معنى. والأرشفةُ تُخرجه من القوائم وتُبقي التاريخ.
 */
export default function Suppliers() {
  const { can } = useAuth()
  const [term, setTerm] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [open, setOpen] = useState<string | 'new' | null>(null)

  const suppliers = useQuery({
    queryKey: ['suppliers', term, showArchived],
    queryFn: () =>
      api<Supplier[]>(
        `/api/v1/suppliers?search=${encodeURIComponent(term)}&includeArchived=${showArchived}`,
      ),

    // النتائجُ السابقةُ تبقى أثناء الكتابة، فلا ترتجف القائمةُ مع كلّ حرف.
    placeholderData: keepPreviousData,
  })

  const mayEdit = can(Perm.supplierEdit)

  return (
    <div className="space-y-4">
      <Panel
        title="المورّدون"
        actions={
          mayEdit && (
            <Button tone="primary" onClick={() => setOpen(open === 'new' ? null : 'new')}>
              {open === 'new' ? 'إلغاء' : 'مورّدٌ جديد'}
            </Button>
          )
        }
      >
        {open === 'new' && (
          <div className="mb-4">
            <SupplierForm onDone={() => setOpen(null)} />
          </div>
        )}

        <div className="mb-4 space-y-2">
          <Field label="بحث" hint="بالاسم أو الرمز أو الهاتف">
            <TextInput value={term} onChange={setTerm} placeholder="اسمُ المورّد…" />
          </Field>

          <label className="flex items-center gap-2 text-xs text-ink-soft">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            أظهِر المؤرشَفين
          </label>
        </div>

        {suppliers.isPending && <Spinner />}
        {suppliers.isError && <ErrorBanner error={suppliers.error} />}
        {suppliers.data?.length === 0 && <Empty>لا مورّدين مطابقين.</Empty>}

        <ul className="space-y-2">
          {(suppliers.data ?? []).map((supplier) => (
            <li key={supplier.id}>
              <button
                type="button"
                onClick={() => setOpen(open === supplier.id ? null : supplier.id)}
                className="block w-full rounded-md border border-line px-3 py-2 text-start hover:bg-surface"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium">{supplier.nameAr}</span>
                  <span className="tabular text-xs text-ink-soft" dir="ltr">
                    {supplier.code}
                  </span>
                </div>

                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                  {supplier.phone && <span dir="ltr">{supplier.phone}</span>}
                  {supplier.currency !== 'JOD' && <span dir="ltr">{supplier.currency}</span>}
                  {!supplier.isActive && <span className="text-warn">مؤرشَف</span>}
                </div>
              </button>

              {open === supplier.id && (
                <div className="mt-2">
                  <SupplierCard supplier={supplier} mayEdit={mayEdit} />
                </div>
              )}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  )
}

/** بطاقةُ مورّدٍ قائم: تفاصيلُه وتعديلُه وأرشفتُه. */
function SupplierCard({ supplier, mayEdit }: { supplier: Supplier; mayEdit: boolean }) {
  const [editing, setEditing] = useState(false)

  if (editing) {
    return <SupplierForm supplier={supplier} onDone={() => setEditing(false)} />
  }

  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <dl className="space-y-1 text-sm">
        {supplier.nameEn && <Row label="بالإنجليزيّة">{supplier.nameEn}</Row>}
        {supplier.phone && <Row label="الهاتف">{supplier.phone}</Row>}
        {supplier.phoneAlt && <Row label="هاتفٌ آخر">{supplier.phoneAlt}</Row>}
        {supplier.email && <Row label="البريد">{supplier.email}</Row>}
        {supplier.address && <Row label="العنوان">{supplier.address}</Row>}
        {supplier.taxNumber && <Row label="الرقمُ الضريبيّ">{supplier.taxNumber}</Row>}
        <Row label="العملة">{supplier.currency}</Row>
        {supplier.paymentTermsDays !== null && (
          <Row label="مدّةُ السداد">{supplier.paymentTermsDays} يومًا</Row>
        )}
        {supplier.notes && <Row label="ملاحظات">{supplier.notes}</Row>}
        {!supplier.isActive && supplier.archiveReason && (
          <Row label="سببُ الأرشفة">{supplier.archiveReason}</Row>
        )}
      </dl>

      {mayEdit && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button onClick={() => setEditing(true)}>تعديل</Button>
          <ArchiveControl supplier={supplier} />
        </div>
      )}
    </div>
  )
}

/** إنشاءٌ أو تعديل — النموذجُ نفسُه، فحقولُه واحدة. */
function SupplierForm({ supplier, onDone }: { supplier?: Supplier; onDone: () => void }) {
  const queryClient = useQueryClient()

  const [nameAr, setNameAr] = useState(supplier?.nameAr ?? '')
  const [nameEn, setNameEn] = useState(supplier?.nameEn ?? '')
  const [phone, setPhone] = useState(supplier?.phone ?? '')
  const [phoneAlt, setPhoneAlt] = useState(supplier?.phoneAlt ?? '')
  const [email, setEmail] = useState(supplier?.email ?? '')
  const [address, setAddress] = useState(supplier?.address ?? '')
  const [taxNumber, setTaxNumber] = useState(supplier?.taxNumber ?? '')
  const [currency, setCurrency] = useState(supplier?.currency ?? 'JOD')
  const [terms, setTerms] = useState(supplier?.paymentTermsDays?.toString() ?? '')
  const [notes, setNotes] = useState(supplier?.notes ?? '')

  const save = useMutation({
    mutationFn: () => {
      const body = {
        nameAr,
        nameEn: nameEn || null,
        phone: phone || null,
        phoneAlt: phoneAlt || null,
        email: email || null,
        address: address || null,
        taxNumber: taxNumber || null,
        currency,
        paymentTermsDays: toNumber(terms),
        notes: notes || null,
      }

      return supplier
        ? api<Supplier>(`/api/v1/suppliers/${supplier.id}`, { method: 'PATCH', body })
        : api<Supplier>('/api/v1/suppliers', { method: 'POST', body })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      onDone()
    },
  })

  const blank = nameAr.trim().length === 0

  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="الاسمُ العربيّ">
          <TextInput value={nameAr} onChange={setNameAr} placeholder="مؤسّسةُ الأمين للتجارة" />
        </Field>

        <Field label="بالإنجليزيّة">
          <TextInput value={nameEn} onChange={setNameEn} dir="ltr" />
        </Field>

        <Field label="الهاتف">
          <TextInput value={phone} onChange={setPhone} dir="ltr" placeholder="0790000000" />
        </Field>

        <Field label="هاتفٌ آخر">
          <TextInput value={phoneAlt} onChange={setPhoneAlt} dir="ltr" />
        </Field>

        <Field label="البريد">
          <TextInput value={email} onChange={setEmail} dir="ltr" />
        </Field>

        <Field label="الرقمُ الضريبيّ">
          <TextInput value={taxNumber} onChange={setTaxNumber} dir="ltr" />
        </Field>

        <Field label="العملة" hint="الدفترُ بالدينار دائمًا، وهذه عملةُ تعامله">
          <TextInput value={currency} onChange={(v) => setCurrency(v.toUpperCase())} dir="ltr" />
        </Field>

        <Field label="مدّةُ السداد (يومًا)">
          <NumberInput value={terms} onChange={setTerms} placeholder="30" />
        </Field>
      </div>

      <div className="mt-3 space-y-3">
        <Field label="العنوان">
          <TextInput value={address} onChange={setAddress} />
        </Field>

        <Field label="ملاحظات">
          <TextArea value={notes} onChange={setNotes} rows={2} />
        </Field>
      </div>

      {save.isError && (
        <div className="mt-3">
          <ErrorBanner error={save.error} />
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button tone="primary" disabled={blank || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'يُحفظ…' : supplier ? 'حفظُ التعديل' : 'إنشاء'}
        </Button>

        <Button onClick={onDone}>إلغاء</Button>
      </div>
    </div>
  )
}

/**
 * الأرشفةُ تحتاج سببًا، وإعادةُ التفعيل لا تحتاج.
 *
 * والسببُ يُطلب في الشاشة <b>وفي الخادم</b>: الخادمُ يردّ 400 بلا سبب، وهذا
 * يمنع أن يصل الموظّفُ إلى الردّ أصلًا.
 */
function ArchiveControl({ supplier }: { supplier: Supplier }) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [asking, setAsking] = useState(false)

  const toggle = useMutation({
    mutationFn: (archived: boolean) =>
      api<Supplier>(`/api/v1/suppliers/${supplier.id}/archive`, {
        method: 'POST',
        body: { archived, reason: archived ? reason : null },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      setAsking(false)
      setReason('')
    },
  })

  if (!supplier.isActive) {
    return (
      <Button disabled={toggle.isPending} onClick={() => toggle.mutate(false)}>
        {toggle.isPending ? 'يُعاد…' : 'إعادةُ التفعيل'}
      </Button>
    )
  }

  if (!asking) {
    return <Button onClick={() => setAsking(true)}>أرشفة</Button>
  }

  return (
    <div className="w-full space-y-2">
      <Banner tone="warn">
        المورّدُ يخرج من القوائم ويبقى في المستندات. ولا يُؤرشَف وله أمرٌ مفتوح.
      </Banner>

      <Field label="سببُ الأرشفة">
        <TextInput value={reason} onChange={setReason} placeholder="توقّف التعامل" />
      </Field>

      {toggle.isError && <ErrorBanner error={toggle.error} />}

      <div className="flex gap-2">
        <Button
          tone="danger"
          disabled={reason.trim().length === 0 || toggle.isPending}
          onClick={() => toggle.mutate(true)}
        >
          {toggle.isPending ? 'يُؤرشَف…' : 'تأكيدُ الأرشفة'}
        </Button>

        <Button onClick={() => setAsking(false)}>تراجع</Button>
      </div>
    </div>
  )
}
